<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <h5 class="section-label mb-2  mt-2">
      {{ $t('Suspicious Event Checks') }}
    </h5>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="8"
          md="8"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="4"
          md="4"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="filter"
              class="d-inline-block mr-1"
              :placeholder="$t('Search...')"
              type="search"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      :items="fraudCheckList.fraudCheckConfigs"
      responsive
      :busy="isBusy"
      :fields="translatedTableHeaders"
      :filter="filter"
      :filter-included-fields="filterOn"
      filter-debounce="100"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
      @filtered="onFiltered"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(displayName)="data">
        {{ data.value }}
      </template>
      <template #cell(triggerRules)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value === true ? 'Enabled' : 'Disabled')"
        >
          {{ data.value === true ? $t('Enabled') : $t('Disabled') }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('UPDATE_SECURITY_CONFIG')"
              @click="onSelectFraudCheckItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span
                class="align-middle ml-50"
              > {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectFraudCheckItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
    >
      <validation-observer ref="fraudCheckRules">
        <b-form v-if="fraudCheckList.fraudCheckConfigs">
          <b-row class="match-height mt-1 mb-2">
            <b-col
              md="12"
              class="d-flex flex-row"
            >
              <h4 style="text-transform: uppercase;">
                {{ fraudCheckList.fraudCheckConfigs[selectedIndex].displayName }}
              </h4>
              <SwitchButton
                v-model="fraudCheckList.fraudCheckConfigs[selectedIndex].triggerRules"
                :label="$t('Trigger Rules')"
                name="trigger-rules"
                class="ml-4"
                :readonly="isReadonly"
                @input="setTriggerRule(fraudCheckList.fraudCheckConfigs[selectedIndex].triggerRules)"
              />
            </b-col>
          </b-row>
          <div v-if="fraudCheckList.fraudCheckConfigs[selectedIndex].triggerRules">
            <b-card
              v-for="(item, index) in fraudCheckList.fraudCheckConfigs[selectedIndex].rules"
              :key="index.toString()"
            >
              <b-card-body>
                <b-row
                  class="d-flex align-items-center"
                >
                  <b-col md="6">
                    <InputField
                      id="rule-name"
                      v-model="item.name"
                      :label="$t('Name')"
                      :placeholder="$t('Enter name')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <b-col
                    md="6"
                    class="mt-2"
                  >
                    <SwitchButton
                      v-model="item.enable"
                      :label="$t('Enabled')"
                      name="enabled"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>
                <b-row>
                  <b-col
                    v-if="item.enable"
                    md="6"
                  >
                    <InputField
                      id="duration"
                      v-model="item.duration"
                      :label="$t('Duration (In minutes)')"
                      :placeholder="$t('Enter duration (In minutes)')"
                      type="number"
                      :error-name="$t('Duration')"
                      rules="required|integer|positive"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>
                <b-row>
                  <b-col
                    v-if="item.enable"
                    md="6"
                    class="mt-2"
                  >
                    <SwitchButton
                      v-model="item.triggerOnAmountThreshold"
                      :label="$t('Limit Amount Threshold')"
                      name="amount-threshold"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <b-col
                    v-if="item.enable && item.triggerOnAmountThreshold"
                    md="6"
                  >
                    <InputNumberField
                      id="trigger-amount"
                      v-model="item.triggerAmount"
                      :label="$t('Limit Amount')"
                      :placeholder="$t('Enter Limit Amount')"
                      :error-name="$t('Limit Amount')"
                      rules="required"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

          </div>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BBadge, BFormInput, BTable, BCardBody,
  BDropdown, BDropdownItem, BModal, BSpinner, BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import { eChannelsPermission } from '@/helpers/permissions'
import getStatusColor from '@/helpers/getStatusColor'
import SwitchButton from '../../../../components/switch/Switch.vue'
import { toKobo, formatAmount } from '@/helpers/formatAmount'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'

export default {
  components: {
    BCard,
    BCardBody,
    BBadge,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BModal,
    BForm,
    BSpinner,
    ValidationObserver,
    SwitchButton,
    InputField,
    InputNumberField,
    TablePagination,
    TableEntries,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      formatAmount,
      isBusy: false,
      showSpinner: false,
      selectedIndex: 0,
      sortBy: 'name',
      getStatusColor,
      required,
      filterOn: [],
      filter: null,
      modalTitle: this.$t('Suspicious Events Check'),
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'displayName', sortable: true, label: 'Display Name' },
        { key: 'triggerRules', sortable: true, label: 'Trigger Rules' },
        { key: 'actions', label: 'Actions' },
      ],
      fraudCheckList: {},
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchFraudChecks()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchFraudChecks()
    },
  },
  mounted() {
    this.fetchFraudChecks()
  },
  methods: {
    ...mapActions({
      getFraudChecks: 'eChannels/getFraudChecksList',
      updateFruadChecks: 'eChannels/updateFraudCheckAction',
    }),

    async fraudCheckAction() {
      try {
        this.isLoading = true
        const formData = {
          ...this.fraudCheckList,
          fraudCheckConfigs: this.fraudCheckList.fraudCheckConfigs.map(item => ({
            ...item,
            rules: item.rules.map(dataItem => ({
              ...dataItem,
              triggerAmount: toKobo(dataItem.triggerAmount),
            })),
          })),
        }

        const response = await this.updateFruadChecks({
          formData, permission: eChannelsPermission.UPDATE_SECURITY_CONFIG,
        })
        const { status } = await response
        if (status === 200) {
          this.fetchFraudChecks()
          this.hideModal('modal-lg')
          const requestText = this.$t('Your request to')
          const eventText = this.$t('Suspicious Events Check was successful')
          this.alertSuccess({ text: `${requestText} ${this.buttonLabel} ${eventText}` })
        }
      } catch (error) {
        this.hideModal('modal-lg')
        error.response.data.errors.forEach(responseError => {
          const requestText = this.$t('Your request to')
          const reasonText = this.$t('Suspicious Events Check failed. Reason')
          this.alertError({ text: `${requestText} ${this.buttonLabel} ${reasonText}: ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.fraudCheckRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const eventText = this.$t('this Suspicious Events Check?')
          this.confirm(this.fraudCheckAction, { text: `${areYouSure} ${this.buttonLabel} ${eventText}` })
        }
      })
    },
    async fetchFraudChecks() {
      try {
        this.showSpinner = true
        // this.isBusy = true
        const response = await this.getFraudChecks({
          permission: eChannelsPermission.VIEW_SECURITY_CONFIG,
        })
        const { status, data } = response
        if (status === 200 && data.fraudCheckConfigs !== null) {
          this.fraudCheckList = {
            ...data,
            fraudCheckConfigs: data.fraudCheckConfigs.map(item => ({
              ...item,
              rules: item.rules.map(dataItem => ({
                ...dataItem,
                triggerAmount: formatAmount(dataItem.triggerAmount),
              })),
            })),
          }
          this.totalCount = this.fraudCheckList?.fraudCheckConfigs?.length
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
        // this.isBusy = false
      }
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalCount = filteredItems.length
      this.currentPage = 1
    },
    async onSelectFraudCheckItem(items, activity, val) {
      this.selectedIndex = this.fraudCheckList.fraudCheckConfigs.findIndex(item => item.name === items.name)
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Suspicious Events Check') : this.$t('View Suspicious Events Check')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    setTriggerRule(val) {
      if (val === false) {
        this.fraudCheckList.fraudCheckConfigs[this.selectedIndex].rules = this.fraudCheckList.fraudCheckConfigs[this.selectedIndex].rules.map(dataItem => ({
          ...dataItem,
          duration: 0,
          enable: false,
          triggerOnAmountThreshold: false,
          triggerAmount: 0,
        }))
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
